import { Stack as FluentStack } from "@fluentui/react";
import { IOverflowSetItemProps, OverflowSet, ResizeGroup } from "@fluentui/react";
import React from "react";
import {isRTL} from "../../../../../styles/language";
import { Media } from "../../../../Media/Media";
import { Stack } from "../../../../Stacks/Stack/Stack";
import { Text } from "../../../../Text/Text";

export interface DrawerHeaderMediaProps {
    readonly mediaImageList: readonly string[];
    readonly mediaLabel: string;
    readonly overflowLabel: string;
    readonly hideHeaderMedia?: boolean;

    /**
     * Id to link with Drawer content
     */
    readonly id?: string;
}

interface OverflowData {
    // tslint:disable-next-line: readonly-array
    readonly primary: IOverflowSetItemProps[];
    // tslint:disable-next-line: readonly-array
    readonly overflow: IOverflowSetItemProps[];
    readonly cacheKey?: string;
}

const computeCacheKey = (primaryControls: readonly IOverflowSetItemProps[]): string =>
    primaryControls.map(control => control.key).toString();

export const DrawerHeaderMedia: React.FC<DrawerHeaderMediaProps> = ({
    mediaImageList,
    overflowLabel,
    mediaLabel,
    hideHeaderMedia,
    id
}) => {

    let computedCacheKey = "";
    const mediaItems: IOverflowSetItemProps[] = mediaImageList && mediaImageList.map((mediaImage, mediaIndex) => {
        computedCacheKey += `item${mediaIndex}`;
        return {
            key: mediaIndex.toString(),
            mediaImage: mediaImage
        };
    });
    const hideHeaderTranslate = isRTL() ? "translateX(100%)" : "translateX(-100%)";

    const hidingItemsStyle: React.CSSProperties = {
        transition:"transform 0.5s ease-out",
        transformOrigin: "left",
        transform: hideHeaderMedia ? hideHeaderTranslate : "translateX(0%)"
    };

    const onRenderItem = (item: IOverflowSetItemProps) =>
    (
        <Stack margin="0px 8px 0px 0px">
            <Media size="md" borderRadius="4px" image={{ imageType: "image", src: item.mediaImage }} height="32px" padding="0px" />
        </Stack>
    );

    const onRenderOverflowItems = (overflowItems: readonly any[] | undefined) =>
    (
        <Stack verticalAlign="center">
            <Text variant="bodySecondaryText">{`+${overflowItems?.length} ${overflowLabel}`}</Text>
        </Stack>
    );

    const onRenderData = (data: OverflowData) => {
        return (
            <Stack childrenGap="17px" verticalAlign="center" horizontal={true}>
                <Text id={id}>{mediaLabel}</Text>
                {/** Added overflow hidden to cover any number of media images and avoid overlap with media label  */}
                <Stack overflow={"hidden"}>
                    <FluentStack style={hidingItemsStyle}>
                        <OverflowSet items={data.primary} onRenderItem={onRenderItem} overflowItems={data.overflow} onRenderOverflowButton={onRenderOverflowItems} />
                    </FluentStack>
                </Stack>
            </Stack>
        );
    };

    const onReduceData = (currentData: OverflowData) => {
        if (currentData.primary.length === 0) {
            return undefined;
        }

        // Push media item into overflow
        const overflow = [...currentData.primary.slice(-1), ...currentData.overflow];

        // Remove the previously mentioned overflowed item from primary
        const primary = currentData.primary.slice(0, -1);

        // Compute cache for performance improvement during resizing
        const cacheKey = computeCacheKey(primary);
        return { primary, overflow, cacheKey };
    };

    const onGrowData = (currentData: OverflowData) => {
        if (currentData.overflow.length === 0) {
            return undefined;
        }

        // Move the top media item from overflow out
        const overflow = [...currentData.overflow.slice(1)];

        // And place it into primary
        const primary = [...currentData.primary, ...currentData.overflow.slice(0, 1)];

        // Compute cache for performance improvement during resizing
        const cacheKey = computeCacheKey(primary);

        return { primary, overflow, cacheKey };
    };

    const mediaData: OverflowData = {
        primary: mediaItems,
        overflow: [],
        cacheKey: computedCacheKey
    };

    return (
        <Stack grow={true}>
            <Stack width="inherit" maxWidth="664px">
                <ResizeGroup onRenderData={onRenderData} data={mediaData} onReduceData={onReduceData} onGrowData={onGrowData} />
            </Stack>
        </Stack>
    );
};
